// The omni-artifact review queue's decision staging and wire-request builders.
// Pure (types + React state only) so it lives here in shared/client and both
// apps drive their review from one implementation; each platform keeps its own
// commit mutation (web `useCommitArtifactDecisions`, native likewise) since
// those bind to per-platform HTTP clients.
import type {
  ArtifactAction,
  ArtifactDecision,
  ArtifactDecisionsReq,
} from "@/types/artifacts.ts";
import { useCallback, useState } from "react";
import type { OmniArtifact } from "./selectOmniArtifacts.ts";
import { decisionArtifactId } from "./toolCallApproval.ts";

/**
 * A decision the user has staged for a single artifact in the omni-artifact
 * review queue: a chosen action, a free-text note, or the answers to a
 * `userResponse` form. Mutually exclusive; an artifact with no entry is
 * "skipped" (the user advanced past it) and is simply omitted from the commit.
 *
 * All three are staged, actions included. A review of three drafts is one thing
 * the user is doing, and it should cost one agent run rather than three racing
 * each other while they are still reading draft two (BIG-7346). What made that
 * read as broken before was silence, not batching: a staged Send has to say so
 * on the card, and the last card's Send has to commit the batch rather than wait
 * for the user to find a separate button (BIG-7171).
 *
 * This is the client-only UI shape — distinct from the wire `ArtifactDecision`,
 * which carries `actionId` rather than the whole action object.
 */
export type StagedDecision =
  | { kind: "action"; action: ArtifactAction }
  | { kind: "note"; text: string }
  | { kind: "form"; values: Record<string, string> };

/**
 * Local staging store for the review queue. Holds the per-artifact decisions
 * until the queue commits them as one batch. Presentation-agnostic: it knows
 * nothing about the active card or navigation.
 */
export function useStagedArtifactDecisions() {
  const [decisions, setDecisions] = useState<ReadonlyMap<string, StagedDecision>>(() => new Map());

  // Replaces outright rather than merging: picking Send after Discard means
  // Send, and an action supersedes a note typed on the same card (they are
  // mutually exclusive by construction).
  const stageAction = useCallback((artifactId: string, action: ArtifactAction) => {
    setDecisions((prev) => new Map(prev).set(artifactId, { kind: "action", action }));
  }, []);

  const stageNote = useCallback((artifactId: string, text: string) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      // Store the raw text so spaces survive while typing (the input is
      // controlled by this value). A note that's blank after trimming isn't a
      // decision — clear it. Trailing whitespace is trimmed at commit time in
      // `buildArtifactDecisionsRequest`.
      if (text.trim()) next.set(artifactId, { kind: "note", text });
      else next.delete(artifactId);
      return next;
    });
  }, []);

  const stageFormValues = useCallback((artifactId: string, values: Record<string, string>) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      // Stage only once the user has answered something; an all-blank form is
      // not a decision (mirrors the empty-note rule). Values are sent verbatim
      // at commit — the server validates per-field.
      if (Object.values(values).some((v) => v.trim() !== "")) {
        next.set(artifactId, { kind: "form", values });
      } else {
        next.delete(artifactId);
      }
      return next;
    });
  }, []);

  const clearDecision = useCallback((artifactId: string) => {
    setDecisions((prev) => {
      if (!prev.has(artifactId)) return prev;
      const next = new Map(prev);
      next.delete(artifactId);
      return next;
    });
  }, []);

  const reset = useCallback(() => setDecisions(new Map()), []);

  return { decisions, stageAction, stageNote, stageFormValues, clearDecision, reset };
}

/** One staged decision as the wire shape, or undefined for a note that is
 *  blank after trimming — not a decision, so it never reaches the server. */
function toWireDecision(artifactId: string, decision: StagedDecision): ArtifactDecision | undefined {
  if (decision.kind === "action") {
    // Only the id travels: the server re-derives the handler path from the
    // stored artifact, so a client-held action can't redirect the mutation.
    return { kind: "action", artifactId, actionId: decision.action.id };
  }
  if (decision.kind === "form") {
    return { kind: "form", artifactId, values: decision.values };
  }
  const note = decision.text.trim();
  return note ? { kind: "note", artifactId, note } : undefined;
}

/**
 * The wire decision to send for this card, or undefined when there is nothing
 * left to send.
 *
 * A form whose answers are already in contributes nothing. Its staged values are
 * then a draft of an answer the user has since given somewhere else — the whole
 * form on the canvas, the lightbox, another device — and committing them would
 * submit the same form twice, the second time with whatever half-finished text
 * the wizard was holding. The review keeps the card either way; it just has
 * nothing left to send for it.
 */
function decisionToSend(
  artifact: OmniArtifact,
  decision: StagedDecision,
): ArtifactDecision | undefined {
  if (decision.kind === "form" && isAlreadyAnswered(artifact)) return undefined;
  // A paired tool call's actions are its form's, so an action names the form. A
  // note is about the call the user was looking at, so it stays with the call.
  const target = decision.kind === "action" ? decisionArtifactId(artifact) : artifact.id;
  return toWireDecision(target, decision);
}

function isAlreadyAnswered(artifact: OmniArtifact): boolean {
  return artifact.artifactType === "userResponse" && artifact.form?.values !== undefined;
}

/**
 * Map the staged decisions to the wire request. Iterates `omniArtifacts` so the
 * order is stable and decisions for artifacts no longer in the set (resolved or
 * superseded) are dropped. Undecided artifacts are omitted — the server leaves
 * them pending. This is the whole of what a review submits: ending one never
 * decides a card the user skipped.
 */
export function buildArtifactDecisionsRequest(
  omniArtifacts: readonly OmniArtifact[],
  decisions: ReadonlyMap<string, StagedDecision>,
): ArtifactDecisionsReq {
  const out: ArtifactDecision[] = [];
  for (const artifact of omniArtifacts) {
    const decision = decisions.get(artifact.id);
    if (!decision) continue;
    const wire = decisionToSend(artifact, decision);
    if (wire) out.push(wire);
  }
  return { decisions: out };
}

/**
 * A short, client-side approximation of the committed message, shown in the
 * optimistic placeholder for the ~1s before the server's canonical merged
 * message reconciles in. Built from what the client already knows (actions,
 * notes, form answers) — not an attempt to mirror the server's copy.
 */
export function buildOptimisticDecisionsContent(
  omniArtifacts: readonly OmniArtifact[],
  decisions: ReadonlyMap<string, StagedDecision>,
): string {
  const lines: string[] = [];
  for (const artifact of omniArtifacts) {
    const decision = decisions.get(artifact.id);
    if (!decision) continue;
    if (decision.kind === "action") {
      const title = artifact.isHeavy ? (artifact.biscuit?.title ?? artifact.artifactTypeTitle) : "";
      lines.push(title ? `${decision.action.label} — ${title}` : decision.action.label);
    } else if (decision.kind === "note") {
      lines.push(decision.text.trim());
    } else {
      const answers = Object.values(decision.values)
        .map((v) => v.trim())
        .filter(Boolean);
      if (answers.length > 0) lines.push(answers.join("; "));
    }
  }
  return lines.filter(Boolean).join("\n");
}
